import type { HandlerAdapter, HandlerResult, HandlerResultHandler } from '../../types.ts';
import type { ServerWebExchange } from '../../../../../types/web/server';
import { AbstractServerResponse } from './response.ts';
import type { HandlerFunction, RouterFunction, ServerRequest } from './types.ts';
import * as handler from '../../handler.ts';
import serverRequest from './request.ts';
import { MATCHING_PATTERN_ATTRIBUTE, REQUEST_ATTRIBUTE, URI_VARIABLES_ATTRIBUTE } from './router.ts';
import { PathPattern } from '../../../util/pattern/path-pattern.ts';
import type { HttpMessageReader, HttpMessageWriter } from '../../../../http/codec/types.ts';
import codecConfig from '../../../../http/codec/config.ts';

export class HandlerFunctionAdapter implements HandlerAdapter {
    supports(handler: unknown): boolean {
        return typeof handler === 'function';
    }
    async handle(exchange: ServerWebExchange, handler: unknown): Promise<HandlerResult> {
        const handlerFunction = handler as HandlerFunction;
        const request = exchange.attribute<ServerRequest>(REQUEST_ATTRIBUTE);
        if (!request) {
            throw new Error('ServerRequest is not found in exchange attributes.');
        }
        return handlerFunction(request).then(response => ({
            handler, returnValue: response
        }));

    }
}

export class ServerResponseResultHandler implements HandlerResultHandler {
    readonly #messageWriters: readonly HttpMessageWriter[] = Array<HttpMessageWriter>();
    constructor(messageWriters: readonly HttpMessageWriter[]) {
        if (!messageWriters || messageWriters.length === 0) {
            throw new Error('messageWriters must not be empty.');
        }
        this.#messageWriters = messageWriters;
    }

    supports(result: HandlerResult): boolean {
        return result.returnValue instanceof AbstractServerResponse;
    }

    get messageWriters(): readonly HttpMessageWriter[] {
        return this.#messageWriters;
    }
    async handle(exchange: ServerWebExchange, result: HandlerResult): Promise<void> {
        const response = result.returnValue as AbstractServerResponse;
        if (response === undefined) {
            throw new Error('No ServerResponse');
        }
        return response.writeTo(exchange, {
            messageWriters: this.messageWriters
        });
    }
}

export class RouterFunctionMapping extends handler.AbstractHandlerMapping {
    #routerFunction?: RouterFunction;
    #messageReaders: readonly HttpMessageReader[] = undefined!;
    constructor(routerFunction?: RouterFunction) {
        super();
        this.#routerFunction = routerFunction;
    }

    set messageReaders(messageReaders: readonly HttpMessageReader[]) {
        this.#messageReaders = messageReaders;
    }

    public init(...routerFunctions: Array<RouterFunction>): void {
        if (this.#messageReaders === undefined || this.#messageReaders.length === 0) {
            this.#messageReaders = codecConfig.serverConfigurer.create().readers;
        }
        if (this.#routerFunction) {
            throw new Error('RouterFunctionMapping is already initialized with a RouterFunction');
        }
        this.#routerFunction = routerFunctions.reduce((a, b) => a.and(b));

    }

    protected async getHandlerInternal(exchange: ServerWebExchange): Promise<unknown | undefined> {
        if (this.#routerFunction === undefined) {
            return; // undefined
        }
        const request = serverRequest.create(exchange, this.#messageReaders);
        const handlerFunction = await this.#routerFunction.route(request)
        if (handlerFunction !== undefined) {
            this.#setAttributes(exchange.attributes, request, handlerFunction);
        }
        return handlerFunction;
    }

    #setAttributes(attributes: Map<string, unknown>, request: unknown, handlerFunction: HandlerFunction): void {
        attributes.set(REQUEST_ATTRIBUTE, request);
        attributes.set(handler.BEST_MATCHING_HANDLER_ATTRIBUTE, handlerFunction);

        const pattern = attributes.get(MATCHING_PATTERN_ATTRIBUTE) as PathPattern;
        if (pattern) {
            attributes.set(handler.BEST_MATCHING_PATTERN_ATTRIBUTE, pattern);
        }
        const uriVariables = attributes.get(URI_VARIABLES_ATTRIBUTE);
        if (uriVariables !== undefined) {
            attributes.set(URI_VARIABLES_ATTRIBUTE, uriVariables);
        }
    }
}
